import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ReactIcons } from "@kalyzee/kalyzee-ui";
import { FullscreenContainer, isTouchEnabled, useOuterClickManyTargets } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { focusIconForLayout, NewTriLayoutContainer, NewTriLayoutContainerProps } from "../../../../../components/player/container/NewTriLayoutContainer";
import { PlayerItem, PlayerRendererProvider } from "../../../../../components/player/interfaces";
import { MasterPlayerWorkBox } from "../../../../../components/workBoxes/MasterPlayerWorkBox";
import { PlayerActionItem, PlayerWorkBoxOverlayContent } from "../../../../../components/workBoxes/PlayerWorkBox";
import { LayoutType } from "../../../../../interfaces/ui";
import { useAppStoreSelector } from "../../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../../contexts/sdk/context";
import { isExpert } from "../../../../../helpers/roles";
import { useBoardMedia } from "../../../../../hooks/board";
import { useGridConfig } from "../../../../../hooks/gridConfig";
import { DEFAULT_INTERACTIVE_RATIO, useInteractiveAspectRatios } from "../../../../../hooks/videoAspectRatio";
import { useAppStoreDispatch } from "../../../../../contexts/appStore/context";
import { useDeviceControlsContext } from "../../../../../contexts/deviceControls/context";
import { pushDeviceControlsCmd } from "../../../../../hooks/deviceControls";
import { useVideoSubscriptions } from "../../../../../hooks/ovenmedia";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { useMeetingUtilsContext } from "../../../../../contexts/meetingUtils/context";

import "./dashboard.css";
import styles from "./dashboard.module.css";

export const MeetingDashboardStudentPage = () => {
  const sdkCtx = useSdkContext();

  const { containerAPI } = useMeetingUtilsContext();
  const { processAction } = useDeviceControlsContext();

  const [meeting] = useMeetingSelector(containerAPI, "meeting");
  const [sessions] = useMeetingSelector(containerAPI, "sessions");
  const [ownSession] = useMeetingSelector(containerAPI, "session");

  const layout = useAppStoreSelector(state => state.layout);
  const meetingOptions = useAppStoreSelector(state => state.meetingOptions);
  const gridConfig = useGridConfig();

  const subscribptionsAPI = useVideoSubscriptions();

  const boardLocalOrRemoteMedia = useBoardMedia();

  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!meeting) return { enabledMedia: [] };
    const studentMeetingOptions = meetingOptions[meeting.id];

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => !enabledVideoSessionsIds?.includes(media.mediaSession.id));

    return { enabledMedia };
  }, [meeting?.id, boardLocalOrRemoteMedia, meetingOptions]);

  const playerItems = useMemo(() => enabledMedia?.map(
    (media) =>
      new PlayerItem(media.type === "local" ? {
        type: "stream",
        mediaStream: media.stream,
        extra: {
          meetingSession: media.session,
          mediaSession: media.mediaSession,
        },
      } : {
        type: "video_session",
        videoSession: media.videoSession,
        extra: {
          meetingSession: media.session,
          mediaSession: media.mediaSession,
        },
      })
  ), [enabledMedia]);

  // Interactive (playerFit) support: auto-detected aspect ratios (for the minimap
  // crop) and an in-memory pan store, both keyed by mediaSession id so the
  // framing survives pagination and layout changes.
  const interactiveRatios = useInteractiveAspectRatios(playerItems);
  const minimapPan = useAppStoreSelector(state => state.minimapPan);
  const dispatch = useAppStoreDispatch();

  // Per-item fullscreen state. In dashboard the wrapper goes around each
  // workbox individually (unlike VOD where it wraps the whole multiplayer),
  // so a single id is enough — only one item can be fullscreen at a time.
  const [fullscreenItemId, setFullscreenItemId] = useState<string | undefined>();

  const onFullscreenChange = useCallback((enabled: boolean) => {
    if (!enabled) setFullscreenItemId(undefined);
  }, []);

  // Touch capability is a device property (not a viewport size). A narrow
  // desktop browser is "mobile" but not touch; a touchscreen laptop is touch
  // but not mobile. The tap-to-reveal flow only applies to actual touch input.
  const isTouch = useMemo(() => isTouchEnabled(), []);

  // On touch devices the overlay-hoverable mode is revealed by the browser's
  // synthetic :hover on tap — and that same tap immediately fires onSelect,
  // jumping the player straight to focused. To match mouse's "hover to
  // reveal, click to focus" two-step we track which side player has been
  // tapped once; only the second tap on the same player actually focuses.
  const [revealedPlayerId, setRevealedPlayerId] = useState<string | undefined>();

  // Persistent ref per player id so a tap outside *every* player clears the
  // revealed state via useOuterClickManyTargets. The map is mutated, never
  // replaced, so the refs stay stable across renders.
  const playerRefsMapRef = useRef(new Map<string, React.RefObject<HTMLDivElement>>());
  const getPlayerRef = useCallback((id: string) => {
    let ref = playerRefsMapRef.current.get(id);
    if (!ref) {
      ref = { current: null };
      playerRefsMapRef.current.set(id, ref);
    }
    return ref;
  }, []);

  const renderPlayer = useCallback((
    item: PlayerItem,
    rendererProvider: PlayerRendererProvider,
    session: MeetingSession,
    mediaSession: MeetingSessionMedia,
    isFocused: boolean,
    isSmall: boolean,
    onFocus: () => void,
  ) => {
    if (!meeting || !containerAPI) return null;

    const id = item.getId();
    const isFullscreen = fullscreenItemId === id;

    const deviceId = mediaSession.deviceId;
    const roomId = meeting.room?.id;
    const isLocal = session.id === ownSession?.id;
    
    const sendControl = (action: "move" | "stopMove" | "zoom" | "stopZoom", content: any) => {
      if (!deviceId) return;
      if (isLocal) {
        processAction(deviceId, action, content);
      } else {
        if (!roomId) return;
        pushDeviceControlsCmd(sdkCtx.client, roomId, deviceId, action, content);
      }
    };
    
    const hasControls = !!mediaSession.extra?.controls && isExpert(ownSession);
    const isSidePlayer = !isFullscreen && !isFocused && layout !== LayoutType.Full;

    const isRevealedOnTouch = isTouch && revealedPlayerId === id;

    const onSelectSidePlayer = () => {
      if (isTouch && !isRevealedOnTouch) {
        setRevealedPlayerId(id);
        return;
      }
      // Keep the revealed state on the tapped player when promoting it to
      // focused — otherwise it would visually drop back to the hoverable
      // overlay even though the user is clearly still engaged with it.
      onFocus();
    };

    const overlayContent: PlayerWorkBoxOverlayContent = isSidePlayer ? {
      type: "selection",
      layout,
      onSelect: onSelectSidePlayer,
    } : hasControls ? {
      type: "controls",
      onUp: (on: boolean) => sendControl(on ? "move" : "stopMove", { direction: "up" }),
      onDown: (on: boolean) => sendControl(on ? "move" : "stopMove", { direction: "down" }),
      onLeft: (on: boolean) => sendControl(on ? "move" : "stopMove", { direction: "left" }),
      onRight: (on: boolean) => sendControl(on ? "move" : "stopMove", { direction: "right" }),
      onZoomIn: (on: boolean) => sendControl(on ? "zoom" : "stopZoom", { direction: "in" }),
      onZoomOut: (on: boolean) => sendControl(on ? "zoom" : "stopZoom", { direction: "out" }),
    } : undefined;

    // Fullscreen toggle lives in the topbar.
    const topbarActions: PlayerActionItem[] = [];
    if (!isFullscreen) {
      topbarActions.push({
        key: "fullscreen",
        icon: <ReactIcons.Expand />,
        onClick: () => setFullscreenItemId(id),
        label: "Plein écran",
      });
    } else {
      topbarActions.push({
        key: "exit-fullscreen",
        icon: <ReactIcons.Shrink />,
        onClick: () => setFullscreenItemId(undefined),
        label: "Quitter le plein écran",
      });
    }

    // On touch devices, taps on the container "lock in" the synthetic hover
    // reveal so the overlay stays visible instead of fading after ~1s.
    const containerProps: React.HTMLAttributes<HTMLDivElement> | undefined =
      isTouch
        ? { onClick: () => setRevealedPlayerId(id) }
        : undefined;

    // Interactive sources (playerFit "interactive") get the crop + minimap
    // feature. PlayerWorkBox owns all of it (toggle, minimap, MinimapCrop context
    // + viewport); here we just enable it and wire the pan to the AppStore.
    const isInteractive = mediaSession.extra?.playerFit === "interactive";
    const minimapRatio = interactiveRatios[mediaSession.id] ?? DEFAULT_INTERACTIVE_RATIO;

    return rendererProvider(
      (children, renderingCtx) => (
        <FullscreenContainer
          useNative
          fullscreen={isFullscreen}
          onFullScreen={onFullscreenChange}
          style={{ width: "100%", height: "100%" }}
        >
          <MasterPlayerWorkBox
            meetingAPI={containerAPI}
            session={session}
            mediaSession={mediaSession}
            overlayMode={isRevealedOnTouch ? "overlay" : "overlay-hoverable"}
            overlayContent={overlayContent}
            isSidePlayer={isSidePlayer}
            minimap={{
              enabled: isInteractive,
              ratio: minimapRatio,
              pan: minimapPan[mediaSession.id],
              onPanChange: (p) => dispatch("setMinimapPan", { id: mediaSession.id, pan: p }),
            }}
            topbarActions={topbarActions}
            isSmall={isSmall}
            isPlayingSound={renderingCtx?.isPlayingSound}
            mediaButtons
            boxProps={containerProps}
            containerRef={getPlayerRef(id)}
          >
            {children}
          </MasterPlayerWorkBox>
        </FullscreenContainer>
      )
    );
  }, [meeting, containerAPI, ownSession, processAction, sdkCtx.client, layout, fullscreenItemId, onFullscreenChange, isTouch, revealedPlayerId, getPlayerRef, interactiveRatios, minimapPan, dispatch]);

  // A tap outside every player container clears the revealed state on touch
  // devices. The hook walks `event.target` against each ref, so taps anywhere
  // inside a player (overlay buttons, video frame…) don't trigger.
  const outsideClickRefs = useMemo(
    () => (playerItems ?? []).map(item => getPlayerRef(item.getId())),
    [playerItems, getPlayerRef],
  );
  useOuterClickManyTargets(outsideClickRefs, () => {
    if (isTouch) setRevealedPlayerId(undefined);
  });

  const triLayoutRenderItem = useCallback<NonNullable<NewTriLayoutContainerProps["renderItem"]>>((
    rendererProvider,
    { item, isFocused, isFullscreen, onFocus },
  ) => {
    const itemData = item.getData();
    const session = itemData.extra?.meetingSession;
    const mediaSession = itemData.extra?.mediaSession;
    if (!session || !mediaSession) return undefined;
    // In Full (grid) mode every cell is equivalent — the residual `focusedItemId`
    // from a previous split layout shouldn't single one tile out with a larger
    // font. Treat all non-fullscreen tiles as small there.
    const isSmall = !isFullscreen && (layout === LayoutType.Full || !isFocused);
    return renderPlayer(item, rendererProvider, session, mediaSession, isFocused, isSmall, onFocus);
  }, [renderPlayer, layout]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    const remoteVideoSessions = enabledMedia
      ?.filter((media) => media.type === "remote")
      .map((media) => (media as any).videoSession);
    subscribptionsAPI.update(remoteVideoSessions.map((media) => ({ session: media, active: true })));
    return () => subscribptionsAPI.update(remoteVideoSessions.map((s) => ({ session: s, active: false })));
  }, [subscribptionsAPI, enabledMedia]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting) return null;
  if (!containerAPI) return null;

  const renderNewLayoutContent = () => {
    if (!playerItems?.length) {
      return (
        <div style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed var(--color-element-text)",
          borderRadius: 20,
        }}>
          <div style={{ color: "var(--color-element-text)" }}>{boardLocalOrRemoteMedia.length ? "Aucun flux sélectionné" : "Aucun flux réceptionné"}</div>
        </div>
      );
    }
    return (
      <NewTriLayoutContainer
        meeting={meeting}
        sessions={sessions ?? []}
        items={playerItems}
        layout={layout}
        gridMode={gridConfig.gridMode}
        pageSize={gridConfig.pageSize}
        autoTileMinPx={gridConfig.autoTileMinPx}
        renderItem={triLayoutRenderItem}
      />
    );
  };

  return (
    <div className={styles.container}>
      {renderNewLayoutContent()}
      {/* {renderFocusContent()} */}
    </div>
  );
};
